import axios from "axios";
import ensureErrorResponse from "@/utils/ensureErrorResponse";
import { recordApiFailure } from "@/utils/errorContext";
import router from "@/router";
import { legacyParamsSerializer } from "./legacyParamsSerializer";
import { requestLogout, requestRefreshToken } from "./authApi";
import {
  captureRefreshSession,
  commitRefreshSession,
  getAuthAccessToken,
  isRefreshSessionCurrent,
  hasSessionChanged,
  isLoginAttemptActive,
} from "./authSession";
import {
  beginExpiryCleanup,
  markAuthFailure,
  markAuthLost,
  purgeLocalSession,
  scheduleRecoveryReload,
  stashScreenStateBeforeClear,
} from "./authReturn";

// axios 1.x는 중첩 객체 params를 bracket notation으로 flatten하지만,
// 백엔드 DTO는 v0 시절의 JSON.stringify 결과(단일 문자열)를 전제로 설계됨.
// 전역 기본 + 인스턴스 모두에 v0 호환 직렬화를 주입해 raw axios.* 사용처까지 커버.
axios.defaults.paramsSerializer = { serialize: legacyParamsSerializer };

// 추후 공용 사용때 해제
// const isLocal = process.env.NODE_ENV === 'development';
// const instance = axios.create({
//   baseURL: isLocal ? '' : process.env.VUE_APP_HOST,
//   withCredentials: true,
// });

// MRI 프리뷰(VUE_APP_MRI_BRIDGE=1 빌드)에서만: MRI가 브릿지로 넣어 둔 브랜치 API 주소를 쓴다. 운영 빌드는 이 값이 없어 그대로.
function resolveApiHost() {
  if (process.env.VUE_APP_MRI_BRIDGE === "1") {
    try {
      const override = window.localStorage.getItem("mri_api_host");
      if (override && /^https:\/\//.test(override)) return override.replace(/\/$/, "");
    } catch (e) {
      // 저장소 접근 불가 — 기본값
    }
  }
  return process.env.VUE_APP_HOST;
}

const instance = axios.create({
  baseURL: resolveApiHost(),
  withCredentials: true,
  paramsSerializer: { serialize: legacyParamsSerializer },
});

instance.interceptors.request.use(
  config => {
    const accessToken = localStorage.getItem("naezip_to_ac_se");
    try {
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }

      return config;
    } catch (err) {
      console.error("[_axios.interceptors.request] config : " + err);
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  },
);

let isRefreshing = false;
let refreshSubscribers = [];

function onRefreshed(accessToken) {
  refreshSubscribers.forEach(callback => callback(accessToken));
  refreshSubscribers = [];
}

function addRefreshSubscriber(callback) {
  refreshSubscribers.push(callback);
}

const REFRESH_TOKEN_ENDPOINT = "/api/v1/user/auth/refresh";
const EXCHANGE_TOKEN_ENDPOINT = "/api/v1/user/auth/exchange";

// NZSC-4015: 마지막 API 실패를 errorContext 에 남긴다(오류 페이지가 참조번호와 함께 보고).
//   - 성공 인터셉터: HTTP 2xx 인데 body.code!==0 (업무 실패 — apiWrapper/직접 호출 모두 이 규약)
//   - 실패 인터셉터: 401 은 refresh 재시도가 있어 **최종 실패 지점**에서만 기록, 그 외는 진입 즉시
// NestJS 예외 본문 3형: { code, message } / { statusCode, message: { code, message } } / { statusCode, message: [검증문장…] }
const apiMessageOf = data => {
  const m = data && typeof data.message === "object" && data.message && !Array.isArray(data.message) ? data.message.message : data?.message;
  if (typeof m === "string") return m;
  if (Array.isArray(m)) return m.filter(x => typeof x === "string").join("; ");
  return undefined;
};

const noteApiError = error => {
  try {
    const { config, response } = error || {};
    recordApiFailure({
      path: config?.url,
      status: response?.status ?? 0,
      code: typeof response?.data?.code === "number" ? response.data.code : undefined,
      message: apiMessageOf(response?.data) || error?.message,
    });
  } catch (e) {
    /* 기록 실패가 응답 처리를 막아선 안 된다 */
  }
};

const tokenFromRequest = request => {
  const authorization = request?.headers?.Authorization;
  return typeof authorization === "string" && authorization.startsWith("Bearer ")
    ? authorization.slice(7)
    : null;
};

const handleRefreshFailure = async (error, failedToken) => {
  isRefreshing = false;
  noteApiError(error); // 401 의 최종 실패 지점(refresh 불가·세션 만료)

  // refresh가 진행되는 동안 카카오 로그인/다른 탭이 새 세션을 저장했다면 실패 주체는
  // 옛 세션이다. 이전 요청과 대기 요청을 종료하고 새 세션은 유지한다.
  if (hasSessionChanged(failedToken)) {
    onRefreshed(null);
    return Promise.reject(error);
  }

  // OAuth 콜백은 새 refresh 쿠키를 발급하는 중이다. 이 구간에 옛 refresh 실패가 logout을
  // 호출하면 응답 순서에 따라 방금 발급된 쿠키까지 지울 수 있으므로 파괴적 정리를 보류한다.
  // marker는 TTL 2분이며, 요청 자체는 reject되어 무한 대기하지 않는다.
  if (isLoginAttemptActive()) {
    onRefreshed(null);
    return Promise.reject(error);
  }

  // 위 비교 직후 다른 탭이 세션을 바꾼 경우까지 한 번 더 확인한다. 로그인 marker는
  // localStorage에 공유되므로 정상 OAuth가 이 비교와 clear 사이에 끼어드는 것도 막는다.
  if (hasSessionChanged(failedToken)) {
    onRefreshed(null);
    return Promise.reject(error);
  }

  onRefreshed(null);

  // NZSC-4245: 여기까지 왔으면 이 브라우저에 유효한 로그인이 없다(만료든 처음부터 비로그인이든). 화면이 곧
  // 오류 화면으로 가려 하면 라우터 관문이 이 표시를 보고 로그인 화면으로 돌린다.
  markAuthFailure();

  // 리프레시 토큰 획득 실패 시 전체 로그아웃 처리.
  if (getAuthAccessToken()) {
    window.console.log("refresh endpoint 401");

    // 무한 리로드 방어: 앱 웹뷰에서 손상/만료 access 토큰이 (앱 쿠키·서버측 리프레시 등으로)
    // 매 로드 재공급되면 "clear→logout→router.go(reload)"가 무한 반복돼 전체 백지가 된다
    // (실사용자 재현 완료). 세션 기준 복구 리로드를 1회로 제한하고, 그 이후엔 리로드하지 않고
    // 익명 상태로 앱을 렌더한다(백지 대신 홈 노출). sessionStorage는 localStorage.clear()에
    // 영향받지 않고 리로드를 넘어 유지되므로 카운터 저장소로 안전하다.
    const AUTH_RECOVER_KEY = "_auth_recover_reloads";
    let recoverReloads = 0;
    try {
      recoverReloads = parseInt(window.sessionStorage.getItem(AUTH_RECOVER_KEY) || "0", 10) || 0;
    } catch (e) {
      /* sessionStorage 접근 실패 무시 */
    }

    // 다방 인입 세션(NZSC-3205): refresh 미발급(24h 단명)이라 만료 시 여기로 온다.
    // 비번/소셜 로그인이 없는 계정이므로 "다방에서 재진입" 안내 후 로그아웃 — 무설명 홈 튕김 방지.
    let isDabangSession = false;
    try {
      isDabangSession = window.sessionStorage.getItem("dabang_entered") === "1";
    } catch (e) {
      /* sessionStorage 접근 실패 무시 */
    }
    if (isDabangSession && recoverReloads < 1) {
      window.alert(
        "이용 시간이 만료되었어요.\n다방 앱에서 내집스캔을 다시 열어주시면 이어서 보실 수 있어요.",
      );
    }

    // NZSC-4245: 정리 전에 "이 탭에서 로그인이 만료됨"을 남긴다(로그인 화면의 만료 문구·최근 로그인 말풍선).
    // 화면 상태는 이 탭에만 맡겨 같은 계정으로 다시 로그인할 때만 되살린다(정리 자체는 지금처럼 전부 지운다).
    // 다방 세션은 소셜 로그인이 없어 로그인 화면으로 보내지 않으므로 둘 다 하지 않는다.
    if (!isDabangSession) {
      markAuthLost();
      stashScreenStateBeforeClear();
    }
    purgeLocalSession(); // 전부 지우되 로그인 방법(kakao|apple — "최근 로그인" 표시용)만 남긴다

    // 리프레시 토큰 클리어도 현재 호스트에 맞는 인증 전송 경로를 사용한다.
    // NZSC-4245: 이 페이지에서 만료 정리가 시작됐다는 표시를 **await 전에** 남긴다. 위 onRefreshed(null) 로 실패한
    // 대기 요청들의 처리부가 이 await 사이에 오류 화면으로 가려 하면, 라우터 관문이 이 표시를 보고 앱 안 이동
    // (메모리의 이전 회원 상태가 저장소에 다시 써짐) 대신 정리가 끝난 뒤 로그인 화면을 풀 로드한다.
    await beginExpiryCleanup(
      requestLogout().catch(() => {
        /* 로그아웃 실패해도 복구 흐름은 계속 */
      }),
    );

    if (recoverReloads < 1) {
      // 정상 만료 복구: 토큰 정리 후 1회 새로고침 → 익명으로 재진입.
      try {
        window.sessionStorage.setItem(AUTH_RECOVER_KEY, String(recoverReloads + 1));
      } catch (e) {
        /* sessionStorage 접근 실패 무시 */
      }
      // NZSC-4245: 바로 새로고침하지 않고 한 박자(같은 틱의 후속 처리) 뒤로 미룬다. 실패를 받은 화면이 그 사이
      // 오류 화면으로 가려 하면 라우터 관문이 이 예약을 취소하고 로그인 화면으로 보낸다. 아무도 가로채지
      // 않으면(로그인이 필요 없는 화면) 지금까지처럼 새로고침해 익명으로 다시 그린다.
      // 새로고침 직전에 한 번 더 비운다 — 정리 도중의 화면 변경이 메모리의 이전 회원 상태를 저장소에 다시 썼을 수 있다.
      scheduleRecoveryReload(() => {
        purgeLocalSession();
        router.go();
      });
    } else {
      // 이미 1회 복구 리로드를 했는데 또 왔다 = 손상 상태가 재공급되는 중.
      // 더 리로드하면 무한 백지 → 리로드하지 않고 익명 렌더 유지.
      window.console.warn("auth recovery loop detected — staying anonymous without reload");
    }
  }
  return Promise.reject(error);
};

instance.interceptors.response.use(
  response => {
    // NZSC-4015: 2xx 이지만 업무 실패(code!==0)도 오류 페이지의 흔한 원인 — 여기서 잡아야 apiWrapper 를
    // 안 쓰는 직접 호출(45파일)까지 덮인다. 응답은 손대지 않는다.
    try {
      const code = response?.data?.code;
      if (typeof code === "number" && code !== 0) {
        recordApiFailure({
          path: response?.config?.url,
          status: response?.status ?? 200,
          code,
          message: typeof response?.data?.message === "string" ? response.data.message : undefined,
        });
      }
    } catch (e) {
      /* noop */
    }
    return response;
  },

  async error => {
    ensureErrorResponse(error);
    const { config, response } = error;
    const originalRequest = config;
    // return Promise.reject(error);

    if (!response || response.status !== 401) noteApiError(error);

    if (response && response.status === 401) {
      // 프리뷰 토큰(NZSC-P-ENC) 요청의 401 은 고객 세션과 무관하다 —
      // refresh 시도·localStorage.clear()·리로드 어느 것도 하면 안 된다
      // (만료된 관리자 미리보기 링크를 로그인 상태로 열면 고객이 로그아웃되던 결함).
      if (originalRequest?.skipAuthRefresh) {
        noteApiError(error);
        return Promise.reject(error);
      }
      if (originalRequest.url === EXCHANGE_TOKEN_ENDPOINT) {
        noteApiError(error);
        return Promise.reject(error);
      } else if (originalRequest.url === REFRESH_TOKEN_ENDPOINT) {
        return handleRefreshFailure(
          error,
          tokenFromRequest(originalRequest) || getAuthAccessToken(),
        );
      } else if (!originalRequest._retry) {
        const requestToken = tokenFromRequest(originalRequest);
        const currentToken = getAuthAccessToken();
        if (requestToken !== currentToken) {
          noteApiError(error);
          return Promise.reject(error);
        }

        window.console.log("isRefreshing", isRefreshing, originalRequest.url);
        if (isRefreshing) {
          originalRequest._retry = true;
          // 리프레시 진행 중인 경우 큐에 요청 추가하고 리프레시 완료 후 재시도.
          return new Promise((resolve, reject) => {
            addRefreshSubscriber(accessToken => {
              if (accessToken) {
                originalRequest.headers["Authorization"] = `Bearer ${accessToken}`;
                resolve(instance(originalRequest));
              } else {
                reject(error);
              }
            });
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;
        const refreshSnapshot = captureRefreshSession();
        const refreshStartedWith = refreshSnapshot.token;

        try {
          // 리프레시 엔드포인트 호출.
          const refreshResponse = await requestRefreshToken();
          const newToken = refreshResponse?.data?.data?.session;
          if (newToken) {
            // 새 엑세스 토큰 획득 시 로컬스토리지에 저장.
            if (!commitRefreshSession(refreshSnapshot, newToken)) {
              onRefreshed(null);
              return Promise.reject(error);
            }

            // 액세스 토큰 획득했으므로 큐에 있는 요청들 재시도.
            onRefreshed(newToken);

            // 액세스 토큰 업데이트 후 기존 요청 재시도.
            originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
            return instance(originalRequest);
          }
          if (!isRefreshSessionCurrent(refreshSnapshot)) {
            onRefreshed(null);
            return Promise.reject(error);
          }
          return handleRefreshFailure(error, refreshStartedWith);
        } catch (refreshError) {
          if (!isRefreshSessionCurrent(refreshSnapshot)) {
            onRefreshed(null);
            return Promise.reject(refreshError);
          }
          ensureErrorResponse(refreshError);
          return handleRefreshFailure(refreshError, refreshStartedWith, originalRequest);
        } finally {
          isRefreshing = false;
        }
      }
    }

    return Promise.reject(error);
  },
);

export default instance;
